// Service worker registration and update detection for the web/PWA app.

import isPwa from "./Utils/isPwa";

export const APP_UPDATE_AVAILABLE = "app-update-available";

const POLL_INTERVAL_BROWSER = 5 * 60 * 1000;
const POLL_INTERVAL_PWA = 60 * 1000;
const MAX_RETRIES = 3;
const RETRY_DELAY = 5000;

const isElectron = () =>
  window.electronAPI && typeof window.electronAPI === "object";

const isProduction = () => process.env.NODE_ENV === "production";

const isServiceWorkerSupported = () => "serviceWorker" in navigator;

const shouldRegisterServiceWorker = () =>
  !isElectron() && isProduction() && isServiceWorkerSupported();

function getPollInterval() {
  return isPwa() ? POLL_INTERVAL_PWA : POLL_INTERVAL_BROWSER;
}

function getBuildId() {
  return process.env.REACT_APP_BUILD_ID || "dev";
}

function dispatchUpdateAvailable(detail) {
  window.dispatchEvent(
    new CustomEvent(APP_UPDATE_AVAILABLE, {
      detail: {
        version: detail.version || process.env.REACT_APP_VERSION || "",
        buildId: detail.buildId || "",
        registration: detail.registration || null,
        source: detail.source || "service-worker",
      },
    })
  );
}

function handleWaitingWorker(registration, source) {
  if (!registration?.waiting) return;
  dispatchUpdateAvailable({
    registration,
    source,
  });
}

function trackInstallingWorker(registration, source) {
  const worker = registration.installing;
  if (!worker) return;

  worker.addEventListener("statechange", () => {
    if (worker.state === "installed" && navigator.serviceWorker.controller) {
      handleWaitingWorker(registration, source);
    }
  });
}

export function register() {
  if (!shouldRegisterServiceWorker()) {
    console.log("[ServiceWorker] Registration skipped");
    return;
  }

  window.addEventListener("load", () => {
    const publicUrl = new URL(process.env.PUBLIC_URL || "", window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      console.warn("[ServiceWorker] PUBLIC_URL origin mismatch");
      return;
    }

    registerValidSW(`${process.env.PUBLIC_URL || ""}/service-worker.js`);
  });
}

function registerValidSW(swUrl) {
  let retryCount = 0;
  let updateCheckInterval = null;

  const attemptRegistration = () => {
    const versionedSwUrl = `${swUrl}?v=${getBuildId()}`;

    navigator.serviceWorker
      .register(versionedSwUrl, { scope: "/", updateViaCache: "none" })
      .then((registration) => {
        console.log("[ServiceWorker] Registered");

        if (registration.waiting) {
          handleWaitingWorker(registration, "waiting-on-load");
        }

        registration.update().catch(() => {});

        if (updateCheckInterval) clearInterval(updateCheckInterval);
        updateCheckInterval = setInterval(() => {
          registration.update().catch(() => {});
        }, getPollInterval());

        const checkOnVisible = () => {
          if (document.visibilityState === "visible") {
            registration.update().catch(() => {});
          }
        };
        document.addEventListener("visibilitychange", checkOnVisible);
        window.addEventListener("focus", checkOnVisible);
        window.addEventListener("pageshow", (event) => {
          if (!event.persisted) {
            registration.update().catch(() => {});
          }
        });

        registration.addEventListener("updatefound", () => {
          trackInstallingWorker(registration, "updatefound");
        });

        navigator.serviceWorker.addEventListener("message", (event) => {
          const data = event.data;
          if (!data) return;
          if (data.type === "UPDATE_AVAILABLE" || data.type === "SW_ACTIVATED") {
            if (registration.waiting || data.type === "UPDATE_AVAILABLE") {
              dispatchUpdateAvailable({
                registration,
                version: data.version,
                buildId: data.buildId,
                source: "service-worker-message",
              });
            }
          }
        });
      })
      .catch((error) => {
        console.error("[ServiceWorker] Registration failed:", error);
        if (retryCount < MAX_RETRIES) {
          retryCount += 1;
          setTimeout(attemptRegistration, RETRY_DELAY * retryCount);
        }
      });
  };

  attemptRegistration();
}

export async function applyPendingUpdate(registration) {
  const waiting = registration?.waiting;
  if (!waiting) return false;

  waiting.postMessage({ type: "SKIP_WAITING" });
  waiting.postMessage({ type: "CLEAR_CACHES" });

  await new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      resolve();
    };
    const onControllerChange = () => done();
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    setTimeout(done, 5000);
  });

  if ("caches" in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    } catch (_) { /* ignore */ }
  }

  const url = new URL(window.location.href);
  url.searchParams.set("_appRefresh", Date.now().toString());
  window.location.replace(url.toString());
  return true;
}

export function unregister() {
  if (isServiceWorkerSupported() && !isElectron()) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => console.error("[ServiceWorker] Unregister failed:", error));
  }
}
